import type { GuidedStep } from '@/lib/menu/guidedSteps'
import type { GuidedState } from '@/lib/menu/guidedFlowReducer'
import { flattenSelectedOptions } from '@/lib/menu/flattenSelectedOptions'
import { effectiveItemCardPrice, type PricingConfig } from '@/lib/utils/pricing'
import type { CartItem } from '@/components/microsite/types'

/** Solo los campos que la función necesita del ítem — evita acoplarse al
 *  shape completo de `GuidedItemFlow`'s `Item` (traducciones, foto, etc.). */
export type MicrositeGuidedItem = {
  id: string
  name: string
  price: number
}

/**
 * Arma la línea que el microsite manda al carrito (extraído de
 * GuidedItemFlow.handleCommit — mismo comportamiento, movido a función pura
 * para poder probar la paridad de dinero contra el CABLEADO real, no contra
 * literales escritos en el test).
 *
 * Invariantes que preserva, sin cambio:
 *  - `price` YA EFECTIVO (`effectiveItemCardPrice(item.price, cfg)`): el
 *    carrito del microsite NO vuelve a aplicar pricing sobre la base al
 *    totalizar (ver RestaurantMicrosite.tsx / OrderSheet.tsx), solo suma.
 *  - `price_delta` de cada modificador YA EFECTIVO (`o.priceDelta`, el que
 *    ya calculó `buildGuidedSteps` vía `effectiveUnitPrice`) — igual que
 *    hace `ItemModal` hoy.
 *  - `option_name`/`name` CANÓNICOS — nunca el traducido por `localizedItem`.
 *
 * Pura: `cartId` entra como parámetro. Nada de `Date.now()`/`Math.random()`
 * adentro — generar el id es responsabilidad de la vista (efecto, no motor).
 */
export function buildMicrositeCartLine(args: {
  item: MicrositeGuidedItem
  steps: GuidedStep[]
  selections: GuidedState['selections']
  note: string
  quantity: number
  cartId: string
  pricingCfg: PricingConfig
}): CartItem {
  const { item, steps, selections, note, quantity, cartId, pricingCfg } = args

  // Lo que el cliente eligió en el bloque Quitar viaja marcado como remoción.
  // /session no trata como agotado lo que se QUITA (lib/online-orders/pricing.ts
  // salta `is_removal`), pero solo si la línea lo dice: sin la marca, "sin
  // cebolla" con la cebolla agotada se rechazaba como si se pidiera cebolla.
  // Misma clave con la que flattenSelectedOptions deduplica.
  const removals = new Set(
    steps.flatMap((s) => (s.kind === 'extras' ? s.remove.map((o) => `${o.groupId}:${o.optionId}`) : [])),
  )

  const modifiers: CartItem['modifiers'] = flattenSelectedOptions(steps, selections).map(({ option: o, quantity: qty }) => ({
    modifier_group_id: o.groupId,
    modifier_option_id: o.optionId,
    option_name: o.name, // CANÓNICO
    price_delta: o.priceDelta, // YA EFECTIVO — igual que hace ItemModal hoy
    quantity: qty,
    // Solo en remociones: lo que se agrega conserva el payload de siempre.
    ...(removals.has(`${o.groupId}:${o.optionId}`) ? { is_removal: true } : {}),
  }))

  return {
    cartId,
    itemId: item.id,
    name: item.name, // CANÓNICO — nunca display.name
    price: effectiveItemCardPrice(item.price, pricingCfg), // el microsite guarda la base YA EFECTIVA
    quantity,
    modifiers,
    specialInstructions: note.trim() || undefined,
  }
}
